import React from 'react'
import { Button, makeStyles } from '@material-ui/core'
import PropTypes from 'prop-types'

const useStyles = makeStyles(theme => ({
  primaryButton: {
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #2DAA67 0%, #228B53 100%)',
    borderRadius: 50,
    zIndex: 1,
    border: 'none',
    position: 'relative',
    overflow: 'hidden',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',

    height: props => props.height,
    padding: '10px 24px',

    [theme.breakpoints.up('md')]: {
      fontSize: '14px',
      padding: '12px 28px'
    },
    [theme.breakpoints.between('sm', 'md')]: {
      fontSize: '13px',
      padding: '10px 22px'
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '12px',
      padding: '8px 18px'
    },
    
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 15px rgba(45, 170, 103, 0.3)',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
      transition: 'left 0.5s ease'
    },

    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(45, 170, 103, 0.4)',
      background: 'linear-gradient(135deg, #3DBC79 0%, #2DAA67 100%)',
      
      '&::before': {
        left: '100%'
      }
    },
    
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 4px 15px rgba(45, 170, 103, 0.3)'
    }
  },
  contactButton: {
    color: '#1a1a1a',
    textDecoration: 'none',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
    borderRadius: 50,
    zIndex: 1,
    whiteSpace: 'nowrap',
    border: 'none',
    position: 'relative',
    overflow: 'hidden',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',

    height: props => props.height,
    padding: '8px 20px',

    [theme.breakpoints.up('md')]: {
      fontSize: '13px',
      padding: '10px 24px'
    },
    [theme.breakpoints.between('sm', 'md')]: {
      fontSize: '12px',
      padding: '8px 18px'
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
      padding: '10px 24px',
      minWidth: 140
    },
    
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 15px rgba(255, 255, 255, 0.2)',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(45, 170, 103, 0.3), transparent)',
      transition: 'left 0.5s ease'
    },

    '&:hover': {
      color: 'white',
      background: 'linear-gradient(135deg, #2DAA67 0%, #228B53 100%)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(45, 170, 103, 0.4)',
      
      '&::before': {
        left: '100%'
      }
    },
    
    '&:active': {
      transform: 'translateY(0)'
    }
  },
  secondaryButton: {
    color: 'rgba(255, 255, 255, 0.9)',
    textDecoration: 'none',
    fontWeight: 600,
    background: 'transparent',
    borderRadius: 50,
    zIndex: 1,
    border: '2px solid rgba(255, 255, 255, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '10px 24px',
    
    [theme.breakpoints.up('md')]: {
      fontSize: '14px'
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '12px',
      padding: '8px 20px'
    },
    
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    
    '&:hover': {
      borderColor: '#2DAA67',
      color: '#2DAA67',
      background: 'rgba(45, 170, 103, 0.1)',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(45, 170, 103, 0.2)'
    }
  },
  accentButton: {
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #FF5F73 0%, #E54559 100%)',
    borderRadius: 50,
    zIndex: 1,
    border: 'none',
    position: 'relative',
    overflow: 'hidden',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '12px 28px',
    
    [theme.breakpoints.up('md')]: {
      fontSize: '14px'
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '12px',
      padding: '10px 22px'
    },
    
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: '0 4px 15px rgba(255, 95, 115, 0.3)',
    
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 25px rgba(255, 95, 115, 0.4)',
      background: 'linear-gradient(135deg, #FF7B8B 0%, #FF5F73 100%)'
    }
  }
}))

export default function PrimaryButton({ text, onClick, isContactButton, variant = 'primary' }) {
  const classes = useStyles()
  
  const getButtonClass = () => {
    if (isContactButton) return classes.contactButton
    switch (variant) {
    case 'secondary':
      return classes.secondaryButton
    case 'accent':
      return classes.accentButton
    default:
      return classes.primaryButton
    }
  }

  return (
    <Button onClick={onClick} className={getButtonClass()}>
      {text.toString().toUpperCase()}
    </Button>
  )
}

PrimaryButton.propTypes = {
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  isContactButton: PropTypes.bool,
  variant: PropTypes.oneOf(['primary', 'secondary', 'accent'])
}
